import { withFolderLock } from '../../utils/folderLock';
import { sanitizeFolderName } from '../../utils/sanitizeFolderName';
import {
  SharePointError,
  SharePointItem,
  SharePointService,
} from './sharePointService';
import { createGraphClient } from './graphApiClient';
import { v4 as uuidv4 } from 'uuid';
import { telemetry } from '@/lib/telemetry';

/**
 * Service that handles Matter-specific SharePoint functionality
 * Creates and manages SharePoint folders for matters
 */

export class MultipleSharePointFoldersError extends Error {
  readonly folders: { id: string; name: string }[];
  constructor(message: string, folders: { id: string; name: string }[]) {
    super(message);
    this.name = 'MultipleSharePointFoldersError';
    this.folders = folders;
  }
}

/**
 * Creates litigation-specific subfolders inside a matter folder
 *
 * @param accessToken - Microsoft Graph API access token
 * @param driveId - SharePoint drive ID
 * @param matterFolderId - The ID of the matter folder
 * @returns Promise that resolves when all subfolders are created
 */
export async function createLitigationSubfolders(
  accessToken: string,
  driveId: string,
  matterFolderId: string,
): Promise<void> {
  const litigationSubfolders = [
    'Case Docs',
    'Drafts',
    'Court Filings',
    'Research',
  ];

  try {
    const service = new SharePointService(
      createGraphClient(accessToken),
      driveId,
    );

    await Promise.all(
      litigationSubfolders.map((folderName) =>
        service.createFolder({
          parentFolderId: matterFolderId,
          name: folderName,
          conflictBehavior: 'rename',
        }),
      ),
    );
  } catch (error) {
    telemetry.error({
      error,
      message: 'Error creating litigation subfolders.',
      extra: { matterFolderId, driveId },
    });
    throw error instanceof Error
      ? error
      : new Error('Failed to create litigation subfolders');
  }
}

export const getOrCreateMatterFolder = async (
  props: (
    | {
        sharePointService: SharePointService;
      }
    | {
        accessToken: string;
        driveId: string;
      }
  ) & {
    matterId: string;
    matterName: string;
  },
): Promise<{ id: string; name: string }> => {
  const uuid = uuidv4();
  const sharePointService =
    'sharePointService' in props
      ? props.sharePointService
      : new SharePointService(
          createGraphClient(props.accessToken),
          props.driveId,
        );
  return withFolderLock(props.matterId, () =>
    _getOrCreateMatterFolder(
      sharePointService,
      props.matterId,
      props.matterName,
      uuid,
    ),
  );
};

const logGetCreateMatterFolderEvent = (uuid: string, message: string) => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }
  console.log(`[${uuid}][GET_CREATE_MATTER_FOLDER] ${message}`);
};

const _getOrCreateMatterFolder = async (
  sharePointService: SharePointService,
  matterId: string,
  matterName: string,
  uuid: string,
): Promise<{ id: string; name: string }> => {
  logGetCreateMatterFolderEvent(uuid, `ENTER _getOrCreateMatterFolder`);

  logGetCreateMatterFolderEvent(uuid, `Will get or create 'Matters' folder...`);
  let mattersFolder: SharePointItem;
  try {
    mattersFolder = await getOrCreateMattersFolder(sharePointService);
  } catch (err) {
    telemetry.error({
      error: err,
      message: 'Error getting or creating matters folder.',
      extra: { matterId, matterName, uuid },
    });
    throw err;
  }

  logGetCreateMatterFolderEvent(uuid, `...Done.`);

  // Check if this matter has a folder in the Matters folder

  logGetCreateMatterFolderEvent(
    uuid,
    `Will look for existing matter folder with ID ${matterId}...`,
  );

  let matterFolder = await getMatterFolder({
    sharePointService,
    mattersFolder,
    matterId,
    uuid,
  });
  if (matterFolder) {
    logGetCreateMatterFolderEvent(
      uuid,
      `...Existing folder found ('${matterFolder.name}').`,
    );
    return { id: matterFolder.id, name: matterFolder.name };
  }

  logGetCreateMatterFolderEvent(
    uuid,
    `...No existing folder found, will create one...`,
  );

  // Sanitize once so the create call and the post-conflict lookup use the
  // exact same name. createFolder also sanitizes internally, but
  // sanitizeFolderName is idempotent.
  const matterFolderName = sanitizeFolderName(`${matterName} ${matterId}`);

  try {
    matterFolder = await sharePointService.createFolder({
      parentFolderId: mattersFolder.id,
      name: matterFolderName,
    });
  } catch (err) {
    // Recovery: Microsoft Graph's `search` endpoint is eventually consistent,
    // so a folder created moments earlier (e.g. by createNewMatter, or by a
    // concurrent tab) may not yet appear in `getMatterFolder`'s search
    // results. In that case the create above fails with `nameAlreadyExists`.
    // Look the existing folder up via a strongly-consistent path-relative
    // GET and return it instead of bubbling the error up to the user.
    if (
      err instanceof SharePointError &&
      err.category === 'itemAlreadyExists'
    ) {
      logGetCreateMatterFolderEvent(
        uuid,
        `createFolder reported nameAlreadyExists; recovering by direct lookup...`,
      );

      const existing = await sharePointService.getChildByName(
        mattersFolder.id,
        matterFolderName,
      );

      if (existing && existing.folder && existing.name.includes(matterId)) {
        telemetry.warn({
          message:
            'Recovered from SharePoint nameAlreadyExists by direct child lookup.',
          extra: { matterId, matterName, uuid, recoveredFolderId: existing.id },
        });
        logGetCreateMatterFolderEvent(
          uuid,
          `...Recovered existing folder '${existing.name}'.`,
        );
        return { id: existing.id, name: existing.name };
      }

      telemetry.error({
        error: err,
        message:
          'SharePoint nameAlreadyExists recovery failed: existing folder could not be looked up by direct child name.',
        extra: {
          matterId,
          matterName,
          uuid,
          lookedUpName: matterFolderName,
          foundItem: existing
            ? {
                id: existing.id,
                name: existing.name,
                isFolder: !!existing.folder,
              }
            : null,
        },
      });
      throw err;
    }

    telemetry.error({
      error: err,
      message: 'Error creating matter folder.',
      extra: { matterId, matterName, uuid },
    });
    throw err;
  }
  logGetCreateMatterFolderEvent(
    uuid,
    `...Done. Folder name is ${matterFolder.name}.`,
  );
  return { id: matterFolder.id, name: matterFolder.name };
};

const getOrCreateMattersFolder = async (
  sharePointService: SharePointService,
): Promise<SharePointItem> => {
  let mattersFolder = await sharePointService.getFolder('Matters');
  if (mattersFolder) {
    return mattersFolder;
  }

  return await sharePointService.createFolder({
    parentFolderId: 'root',
    name: 'Matters',
  });
};

const getMatterFolder = async (props: {
  sharePointService: SharePointService;
  mattersFolder: SharePointItem;
  matterId: string;
  uuid: string;
}): Promise<SharePointItem | undefined> => {
  const { sharePointService, mattersFolder, matterId, uuid } = props;
  const mattersFolderItems = await sharePointService.searchFolderContents(
    mattersFolder.id,
    matterId,
  );

  // To be safe, first find all folders with this matter's ID.
  const matterFolders = mattersFolderItems.filter((item) => {
    return item.folder && item.name.includes(`${matterId}`);
  });

  if (matterFolders.length > 1) {
    logGetCreateMatterFolderEvent(
      uuid,
      `ERROR: Found multiple SharePoint folders for matter ${matterId}: ${matterFolders
        .map((f) => ({ id: f.id, name: f.name }))
        .join(', ')}`,
    );
    throw new MultipleSharePointFoldersError(
      `Multiple SharePoint folders found for matter ${matterId}.`,
      matterFolders.map((f) => ({ id: f.id, name: f.name })),
    );
  }

  logGetCreateMatterFolderEvent(
    uuid,
    `Found ${matterFolders.length} folders for matter ${matterId}`,
  );

  return matterFolders[0];
};
